/**
 * Full-screen receipt viewer.
 *
 * Shown in-page rather than opened in a new tab. Two reasons: the endpoint needs
 * an Authorization header, so the bytes have to be fetched rather than navigated
 * to; and on a phone an overlay beats a new tab, which popup blockers dislike
 * when it is opened after an await.
 *
 * `receipt` is { url, type, label } where url is an object URL. The parent owns
 * that URL and must revoke it on close.
 */
export default function ReceiptViewer({ receipt, onClose }) {
    if (!receipt) return null

    const isPdf = receipt.type === 'application/pdf'

    return (
        <div
            className="fixed inset-0 z-50 bg-black bg-opacity-80 flex flex-col"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Receipt"
        >
            <div className="flex items-center justify-between gap-3 p-3 text-white shrink-0">
                <span className="text-sm truncate">{receipt.label}</span>
                <div className="flex items-center gap-3 shrink-0">
                    {/* A PDF in an iframe is unreadable on a phone, so offer the
                        file itself as well. */}
                    <a
                        href={receipt.url}
                        download
                        onClick={e => e.stopPropagation()}
                        className="text-sm underline opacity-90 hover:opacity-100"
                    >
                        Download
                    </a>
                    <button
                        onClick={onClose}
                        className="text-3xl leading-none px-2 -my-1"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
            </div>

            <div
                className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-3"
                onClick={e => e.stopPropagation()}
            >
                {isPdf ? (
                    <iframe src={receipt.url} title="Receipt" className="w-full h-full bg-white rounded" />
                ) : (
                    <img
                        src={receipt.url}
                        alt="Receipt"
                        className="max-w-full max-h-full object-contain"
                    />
                )}
            </div>

            <p className="text-center text-white text-xs opacity-60 pb-3 shrink-0">
                Tap outside the receipt to close
            </p>
        </div>
    )
}
